import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { useChangePassword } from '@/api-services/hooks/useAuth';
import {
  Lock,
  Eye,
  EyeOff,
  ArrowLeft,
  CheckCircle,
  Shield,
  AlertTriangle,
  RefreshCw,
  Clock,
  Key,
  Smartphone
} from 'lucide-react';

const ChangePasswordPage = () => {
  const navigate = useNavigate();
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [lastPasswordChange, setLastPasswordChange] = useState('2024-01-10');

  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    enableTwoFactor: false,
    requireEmailChange: false,
    logOutOtherSessions: true,
    notifySecurityChange: true
  });

  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    feedback: '',
    color: 'bg-red-500'
  });

  const changePasswordMutation = useChangePassword({
    onSuccess: () => {
      toast.success('Password changed successfully! Your account is now more secure.');
      setTimeout(() => {
        navigate('/admin/dashboard');
      }, 1500);
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to change password. Please verify your current password.');
    },
  });

  const calculatePasswordStrength = (password) => {
    let score = 0;
    let feedback = '';
    let color = 'bg-red-500';

    if (password.length === 0) {
      return { score: 0, feedback: 'Enter a password', color: 'bg-gray-300' };
    }

    // Length check
    if (password.length >= 8) score += 1;
    if (password.length >= 12) score += 1;

    // Character variety
    if (/[a-z]/.test(password)) score += 1;
    if (/[A-Z]/.test(password)) score += 1;
    if (/[0-9]/.test(password)) score += 1;
    if (/[^a-zA-Z0-9]/.test(password)) score += 1;

    // Determine strength
    if (score <= 2) {
      feedback = 'Weak password';
      color = 'bg-red-500';
    } else if (score <= 4) {
      feedback = 'Fair password';
      color = 'bg-yellow-500';
    } else if (score <= 5) {
      feedback = 'Good password';
      color = 'bg-blue-500';
    } else {
      feedback = 'Strong password';
      color = 'bg-green-500';
    }

    return { score, feedback, color };
  };

  const handlePasswordChange = (password) => {
    setFormData(prev => ({ ...prev, newPassword: password }));
    setPasswordStrength(calculatePasswordStrength(password));
  };

  const validatePasswordChange = () => {
    if (!formData.currentPassword) {
      toast.error('Please enter your current password');
      return false;
    }

    if (!formData.newPassword || !formData.confirmPassword) {
      toast.error('Please fill in all password fields');
      return false;
    }

    if (formData.newPassword === formData.currentPassword) {
      toast.error('New password must be different from current password');
      return false;
    }

    if (formData.newPassword.length < 8) {
      toast.error('Password must be at least 8 characters long');
      return false;
    }

    if (passwordStrength.score < 3) {
      toast.error('Please choose a stronger password');
      return false;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return false;
    }

    return true;
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();

    if (!validatePasswordChange()) return;

    try {
      await changePasswordMutation.mutate({
        currentPassword: formData.currentPassword,
        newPassword: formData.newPassword,
        confirmPassword: formData.confirmPassword,
      });
    } catch (error) {
      // Error handling is done in the mutation's onError callback
    }
  };

  const passwordRequirements = [
    { text: 'At least 8 characters', met: formData.newPassword.length >= 8 },
    { text: 'Contains uppercase letter', met: /[A-Z]/.test(formData.newPassword) },
    { text: 'Contains lowercase letter', met: /[a-z]/.test(formData.newPassword) },
    { text: 'Contains a number', met: /[0-9]/.test(formData.newPassword) },
    { text: 'Contains special character', met: /[^a-zA-Z0-9]/.test(formData.newPassword) },
    { text: 'Different from current password', met: formData.newPassword !== formData.currentPassword }
  ];

  const activeSessions = [
    { device: 'Chrome on Windows', location: 'New York, US', lastActive: '2 hours ago', current: true },
    { device: 'Safari on iPhone', location: 'Boston, US', lastActive: '1 day ago', current: false },
    { device: 'Android App', location: 'Los Angeles, US', lastActive: '3 days ago', current: false }
  ];

  return (
    <div className="min-h-screen bg-gradient-subtle">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link to="/admin/dashboard" className="inline-flex items-center text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Link>
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="flex items-center space-x-3 mb-8">
            <div className="w-12 h-12 bg-gradient-primary rounded-xl flex items-center justify-center">
              <Key className="w-7 h-7 text-primary-foreground" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-foreground">Security Settings</h1>
              <p className="text-muted-foreground">Manage your account security and password</p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Password Change Form */}
              <Card className="border-border/50 shadow-elegant">
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <Lock className="w-5 h-5" />
                    <span>Change Password</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleChangePassword} className="space-y-6">
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="currentPassword">Current Password</Label>
                        <div className="relative">
                          <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          <Input
                            id="currentPassword"
                            type={showCurrentPassword ? 'text' : 'password'}
                            placeholder="Enter your current password"
                            className="pl-10 pr-10"
                            value={formData.currentPassword}
                            onChange={(e) => setFormData(prev => ({ ...prev, currentPassword: e.target.value }))}
                            required
                          />
                          <button
                            type="button"
                            onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                          >
                            {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="newPassword">New Password</Label>
                        <div className="relative">
                          <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          <Input
                            id="newPassword"
                            type={showNewPassword ? 'text' : 'password'}
                            placeholder="Enter your new password"
                            className="pl-10 pr-10"
                            value={formData.newPassword}
                            onChange={(e) => handlePasswordChange(e.target.value)}
                            required
                          />
                          <button
                            type="button"
                            onClick={() => setShowNewPassword(!showNewPassword)}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                          >
                            {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>

                        {/* Password Strength Indicator */}
                        {formData.newPassword && (
                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <span className="text-xs text-muted-foreground">Password Strength</span>
                              <span className={`text-xs font-medium ${
                                passwordStrength.score <= 2 ? 'text-red-500' :
                                passwordStrength.score <= 4 ? 'text-yellow-500' :
                                'text-green-500'
                              }`}>
                                {passwordStrength.feedback}
                              </span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div
                                className={`h-2 rounded-full transition-all duration-300 ${passwordStrength.color}`}
                                style={{ width: `${(passwordStrength.score / 6) * 100}%` }}
                              />
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="confirmPassword">Confirm New Password</Label>
                        <div className="relative">
                          <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          <Input
                            id="confirmPassword"
                            type={showConfirmPassword ? 'text' : 'password'}
                            placeholder="Confirm your new password"
                            className="pl-10 pr-10"
                            value={formData.confirmPassword}
                            onChange={(e) => setFormData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                            required
                          />
                          <button
                            type="button"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                          >
                            {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>

                      {/* Password Requirements */}
                      <div className="bg-muted/50 rounded-lg p-4">
                        <p className="text-sm font-medium mb-3">Password Requirements:</p>
                        <ul className="space-y-2">
                          {passwordRequirements.map((requirement, index) => (
                            <li key={index} className="flex items-center space-x-2 text-sm">
                              <CheckCircle className={`w-4 h-4 ${
                                requirement.met ? 'text-green-500' : 'text-gray-300'
                              }`} />
                              <span className={requirement.met ? 'text-green-600' : 'text-gray-500'}>
                                {requirement.text}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Security Options */}
                      <div className="space-y-3 pt-4 border-t">
                        <h3 className="text-sm font-medium text-foreground">Additional Security Options</h3>

                        <div className="flex items-start space-x-2">
                          <Checkbox
                            id="logOutOthers"
                            checked={formData.logOutOtherSessions}
                            onCheckedChange={(checked) =>
                              setFormData(prev => ({
                                ...prev,
                                logOutOtherSessions: checked === true,
                              }))
                            }
                          />
                          <Label htmlFor="logOutOthers" className="text-sm leading-5">
                            Log out from all other devices (recommended)
                          </Label>
                        </div>

                        <div className="flex items-start space-x-2">
                          <Checkbox
                            id="notifyChange"
                            checked={formData.notifySecurityChange}
                            onCheckedChange={(checked) =>
                              setFormData(prev => ({
                                ...prev,
                                notifySecurityChange: checked === true,
                              }))
                            }
                          />
                          <Label htmlFor="notifyChange" className="text-sm leading-5">
                            Send security notification to my email
                          </Label>
                        </div>

                        <div className="flex items-start space-x-2">
                          <Checkbox
                            id="enableTwoFactor"
                            checked={formData.enableTwoFactor}
                            onCheckedChange={(checked) =>
                              setFormData(prev => ({
                                ...prev,
                                enableTwoFactor: checked === true,
                              }))
                            }
                          />
                          <Label htmlFor="enableTwoFactor" className="text-sm leading-5">
                            Enable two-factor authentication
                          </Label>
                        </div>
                      </div>
                    </div>

                    <Button
                      type="submit"
                      className="w-full bg-gradient-primary hover:opacity-90 h-12"
                      disabled={changePasswordMutation.loading}
                    >
                      {changePasswordMutation.loading ? (
                        <>
                          <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                          Changing Password...
                        </>
                      ) : (
                        <>
                          <Shield className="w-5 h-5 mr-2" />
                          Change Password
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Security Tips */}
              <Card className="border-border/50 bg-primary/5">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center space-x-2">
                    <Shield className="w-5 h-5" />
                    <span>Security Tips</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-start space-x-2">
                    <CheckCircle className="w-4 h-4 text-primary mt-0.5" />
                    <span className="text-sm text-muted-foreground">Use a unique password for each account</span>
                  </div>
                  <div className="flex items-start space-x-2">
                    <CheckCircle className="w-4 h-4 text-primary mt-0.5" />
                    <span className="text-sm text-muted-foreground">Enable two-factor authentication</span>
                  </div>
                  <div className="flex items-start space-x-2">
                    <CheckCircle className="w-4 h-4 text-primary mt-0.5" />
                    <span className="text-sm text-muted-foreground">Review active sessions regularly</span>
                  </div>
                  <div className="flex items-start space-x-2">
                    <CheckCircle className="w-4 h-4 text-primary mt-0.5" />
                    <span className="text-sm text-muted-foreground">Keep your recovery info updated</span>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChangePasswordPage;
